import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  Collapse,
  VStack,
  HStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiBarChart2, FiChevronDown, FiChevronRight, FiCalendar, FiTarget, FiTrendingUp, FiLock, FiStar, FiClock } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../constant";
import { usePlanRestriction } from "../context/PlanRestrictionContext";
import { useAccessControl } from "../hooks/useAccessControl";

const ReportsDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { canAccessFeature } = usePlanRestriction();
  const { isBranchUser, canAccessPayroll, canAccessPayrollFeature } = useAccessControl();

  const hoverBgColor = useColorModeValue("gray.50", "gray.700");
  const activeBgColor = useColorModeValue("blue.50", "blue.900");
  const activeColor = useColorModeValue("blue.600", "blue.200");

  const isActive = pathname.startsWith('/report');

  const reportItems = [
    {
      name: "Attendance Report",
      path: ROUTES.attendanceReport,
      icon: FiCalendar,
      description: "View attendance records and patterns",
      canAccess: () => true // Available to all users
    },
    {
      name: "Productivity Reports",
      path: ROUTES.productivityReport,
      icon: FiTarget,
      description: "Analyze employee productivity metrics",
      canAccess: () => !isBranchUser() // Branch users cannot access
    },
    {
      name: "Performance Reports",
      path: ROUTES.performanceReport,
      icon: FiTrendingUp,
      description: "Track performance goals and insights",
      canAccess: () => !isBranchUser() // Branch users cannot access
    },
    {
      name: "Overtime Reports",
      path: ROUTES.overtimeReport,
      icon: FiClock,
      description: "Comprehensive overtime analytics and insights",
      canAccess: () => true // Available to all users
    }
  ];

  // Filter out inaccessible reports
  const accessibleReports = reportItems.filter(item => item.canAccess());

  const handleItemClick = (item) => {
    navigate(item.path);
    setIsOpen(false);
  };

  return (
    <Box>
      {/* Main Reports Button */}
      <Flex
        align="center"
        justify="space-between"
        p={3}
        borderRadius="md"
        cursor="pointer"
        bg={isActive ? activeBgColor : "transparent"}
        color={isActive ? activeColor : "gray.700"}
        _hover={{ bg: hoverBgColor }}
        onClick={() => setIsOpen(!isOpen)}
        transition="all 0.2s"
      >
        <HStack spacing={3}>
          <Icon as={FiBarChart2} size={18} />
          <Text fontSize="sm" fontWeight="500">
            Reports
          </Text>
        </HStack>
        <Icon
          as={isOpen ? FiChevronDown : FiChevronRight}
          size={16}
          transition="transform 0.2s"
          transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
        />
      </Flex>

      {/* Dropdown Items */}
      <Collapse in={isOpen} animateOpacity>
        <VStack spacing={1} align="stretch" mt={2} ml={6}>
          {accessibleReports.map((item) => {
            const isItemActive = pathname === item.path;
            return (
              <Flex
                key={item.path}
                align="center"
                p={2}
                borderRadius="md"
                cursor="pointer"
                bg={isItemActive ? activeBgColor : "transparent"}
                color={isItemActive ? activeColor : "gray.600"}
                _hover={{ bg: hoverBgColor }}
                onClick={() => handleItemClick(item)}
                transition="all 0.2s"
              >
                <HStack spacing={2} flex={1}>
                  <Icon as={item.icon} size={16} />
                  <VStack align="start" spacing={0} flex={1}>
                    <Text fontSize="sm" fontWeight="500">
                      {item.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                      {item.description}
                    </Text>
                  </VStack>
                </HStack>
              </Flex>
            );
          })}
        </VStack>
      </Collapse>
    </Box>
  );
};

export default ReportsDropdown; 